import { URL_ACCOUNT_COMPANY_PROFILE_CLAIM } from '@seek/adv-constants';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Columns,
  Column,
  Stack,
  Text,
  ButtonIcon,
  IconClear,
  Actions,
} from 'braid-design-system';
import { useEffect, useState } from 'react';

import { analyticsEvents } from 'src/constants/analytics';
import { TRACKING_DASHBOARD_MAIN } from 'src/constants/tracking';
import { trackPageInteraction } from 'src/modules/AnalyticsTracker';
import { urlResolver } from 'src/utils/config';

import TrackingButtonLink from '../TrackingButtonLink/TrackingButtonLink';

import translations from './.vocab';
import { ClaimNudgeImage } from './ClaimNudgeImage.svg';
import { hideNudge, isNudgeHidden } from './hideNudge';

import * as styles from './ClaimCompanyNudge.css';

export const ClaimCompanyNudge = () => {
  const { t } = useTranslations(translations);

  // Needed for closing the nudge.
  const [displayNudge, setDisplayNudge] = useState<boolean>(!isNudgeHidden());

  const nudgeType = 'Claim Profile';

  const onDismiss = () => {
    trackPageInteraction(
      analyticsEvents.TRACK_NUDGE_DISSMISSED,
      TRACKING_DASHBOARD_MAIN,
      { nudgeType },
    );

    hideNudge();
    setDisplayNudge(false);
  };

  useEffect(() => {
    trackPageInteraction(
      analyticsEvents.TRACK_NUDGE_VIEWED,
      TRACKING_DASHBOARD_MAIN,
      { nudgeType },
    );
  }, []);

  if (!displayNudge) {
    return null;
  }

  return (
    <Box padding="large" borderRadius="large" background="formAccentSoft">
      <Columns reverse space="gutter" collapseBelow="tablet">
        <Column width="content">
          <Columns space="xsmall">
            <Column>
              <Box
                background="surface"
                borderRadius="full"
                padding={{ mobile: 'xsmall', tablet: 'small' }}
                display="inlineBlock"
              >
                <Box className={styles.svgContainer}>
                  <ClaimNudgeImage />
                </Box>
              </Box>
            </Column>
            <Column width="content">
              <ButtonIcon
                variant="transparent"
                icon={<IconClear />}
                label="Close"
                bleed
                id="close-claim-company-nudge"
                onClick={onDismiss}
              />
            </Column>
          </Columns>
        </Column>
        <Column>
          <Stack space="medium">
            <Text size="large" weight="strong">
              {t('Claim company nudge heading')}
            </Text>
            <Stack space="small">
              <Text size="standard">
                {t(
                  'Your free company profile showcases whats great about your company and lets people leave reviews.',
                )}
              </Text>
              <Text size="standard">
                {t(
                  'Claim your profile now to make updates and respond to reviews',
                )}
              </Text>
            </Stack>
            <Actions>
              <TrackingButtonLink
                href={urlResolver({ path: URL_ACCOUNT_COMPANY_PROFILE_CLAIM })}
                target="_blank"
                rel="noopener"
                size="small"
                trackingEventName={analyticsEvents.TRACK_NUDGE_PRESSED}
                trackingActionOrigin={TRACKING_DASHBOARD_MAIN}
                trackingData={{
                  nudgeType,
                }}
              >
                {t('Claim company nudge button')}
              </TrackingButtonLink>
            </Actions>
          </Stack>
        </Column>
      </Columns>
    </Box>
  );
};
